/**
 * One NEAR Intents account (blackbeard/observer#44).
 *
 * Two kinds of figure, and the page keeps them apart. The balances are read
 * from `intents.near` now and are complete: every token the account holds
 * there. The history is this observer's index, which follows one token, so it
 * lists what the account did with that token and nothing else — a page that
 * looked like a whole history and was not would be the worse failure.
 *
 * It says what the chain links and no more. A bridge deposit names the Quantus
 * transaction it was minted against because the bridge wrote that hash into
 * the mint; a withdrawal names the Quantus address because the withdrawal
 * memo does. Who is behind the account is not something either chain records,
 * and the page does not guess.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { IntentsAccount } from '../api/generated/IntentsAccount'
import type { IntentsAccountEvent } from '../api/generated/IntentsAccountEvent'
import { RequestFailed, fetchIntentsAccount } from '../api/rest'
import { utc, when } from '../lib/format'
import { accountKindText, amountText, amountTitle, shortNear } from '../lib/near'
import { href } from '../lib/routes'
import { tokenLabel } from '../lib/tokens'
import { Field } from './Field'

const EXPLORER = 'https://nearblocks.io/address/'
const REFRESH_MS = 60_000

const KIND: Record<string, string> = {
  bridge_deposit: 'bridged in from Quantus',
  bridge_withdrawal: 'withdrawn to Quantus',
  trade: 'trade',
  transfer_in: 'received',
  transfer_out: 'sent',
}

export function IntentsAccountPanel({ chain, account }: { chain: string; account: string }) {
  const [data, setData] = useState<IntentsAccount | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    setError(null)
    const load = () =>
      fetchIntentsAccount(chain, account, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain, account])

  const asset = data
    ? tokenLabel(data.asset, data.tokens.find((t) => t.token === data.asset)?.symbol)
    : 'the token'

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">NEAR Intents account</h2>
        <span className="eyebrow mono-wrap" title={account}>
          {account}
        </span>
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !data && <p className="empty">Reading the account…</p>}

      {data && (
        <>
          <div className="node-grid fit">
            <Field label="Account" value={shortNear(data.account)} title={data.account} />
            <Field label="Kind" value={accountKindText(data.kind)} />
            <Field
              label="Intent signing keys"
              value={String(data.public_keys.length)}
              note="registered with intents.near"
              title={data.public_keys.join('\n') || undefined}
            />
            <Field
              label="Tokens held"
              value={data.balances === null ? '—' : String(data.balances.length)}
              note={`inside intents.near, read ${when(data.balances_at)}`}
            />
          </div>

          {data.balances === null && (
            <p className="panel-note">
              The Intents contract could not be asked for balances just now.
            </p>
          )}
          {data.balances !== null && data.balances.length > 0 && (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">Balances inside intents.near</caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      Token
                    </th>
                    <th scope="col">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.balances.map((b) => (
                    <tr key={b.token}>
                      <td className="left" title={b.token}>
                        {tokenLabel(b.token, data.tokens.find((t) => t.token === b.token)?.symbol)}
                      </td>
                      <td className="numeral" title={amountTitle(b.amount, b.token, data.tokens)}>
                        {amountText(b.amount, b.token, data.tokens)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="panel-head" style={{ borderTop: 'var(--rule)' }}>
            <h3 className="panel-title">{asset} activity</h3>
            <span className="eyebrow">
              {data.history.length
                ? `${data.history.length} indexed, newest first`
                : 'none indexed'}
            </span>
          </div>
          {data.history.length > 0 && (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  What this observer indexed the account doing with {asset}
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      When
                    </th>
                    <th scope="col" className="left">
                      What
                    </th>
                    <th scope="col">Amount</th>
                    <th scope="col" className="left">
                      Other side
                    </th>
                    <th scope="col" className="left">
                      NEAR tx
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h) => (
                    <tr key={`${h.tx_hash}-${h.kind}-${h.amounts.map((a) => a.amount).join()}`}>
                      <td className="left numeral event-when" title={utc(h.at)}>
                        {when(h.at)}
                      </td>
                      <td className="left">{KIND[h.kind] ?? h.kind}</td>
                      <td className="numeral">
                        {h.amounts.map((a, i) => (
                          <div key={i} title={amountTitle(a.amount, a.token, data.tokens)}>
                            {amountText(a.amount, a.token, data.tokens)}
                          </div>
                        ))}
                      </td>
                      <td className="left">
                        <OtherSide chain={chain} event={h} />
                      </td>
                      <td className="left">
                        <Link to={href({ chain, nearTx: h.tx_hash })} title={h.tx_hash}>
                          {h.tx_hash.slice(0, 8)}…
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="panel-note">
            Balances are every token the account holds inside intents.near, read now. The activity
            is only what this observer indexes — {asset} bridged in and out, traded and transferred
            on NEAR Intents — and not the account&apos;s whole history, which spans tokens this site
            does not follow. Nothing here says who is behind the account: neither chain records
            that. Also on{' '}
            <a href={`${EXPLORER}${data.account}`} target="_blank" rel="noreferrer">
              nearblocks
            </a>
            .
          </p>
        </>
      )}
    </section>
  )
}

function OtherSide({ chain, event: h }: { chain: string; event: IntentsAccountEvent }) {
  if (h.quantus_tx) {
    const label = `Quantus tx ${h.quantus_tx.slice(0, 10)}…`
    return h.quantus_height !== null ? (
      <Link to={href({ chain, tx: h.quantus_tx })} title={h.quantus_tx}>
        {label}
      </Link>
    ) : (
      <span title={h.quantus_tx}>{label}</span>
    )
  }
  if (h.quantus_address) {
    return (
      <Link to={href({ chain, account: h.quantus_address })} title={h.quantus_address}>
        {shortNear(h.quantus_address)}
      </Link>
    )
  }
  if (h.counterparty) {
    return (
      <Link to={href({ chain, intentsAccount: h.counterparty })} title={h.counterparty}>
        {shortNear(h.counterparty)}
      </Link>
    )
  }
  return <>—</>
}
